import { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { api } from '../api';
import { StatusBadge } from './ui';

interface TerminalProps {
  /** The shell of an environment's SSH connection, or… */
  environmentId?: string;
  /** …of a server, with the key saved for it. One of the two. */
  serverId?: string;
  /** What the shell is on, for the title and the greeting. */
  environmentName: string;
  /** One-press commands under the bar, typed into the shell as if entered (CO-190). */
  shortcuts?: TerminalShortcut[];
}

export interface TerminalShortcut {
  key: string;
  label: string;
  /** The line typed into the shell, Enter included. */
  command: string;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/** xterm paints its own canvas, so it gets the terminal surface token as a
 * colour value rather than through CSS. */
function terminalBackground(): string {
  const value = typeof window === 'undefined' ? '' : getComputedStyle(document.documentElement).getPropertyValue('--surface-terminal').trim();
  return value || '#0e1013';
}

export function Terminal({ environmentId, serverId, environmentName, shortcuts }: TerminalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const connect = useCallback(() => {
    if (!containerRef.current) return;

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (xtermRef.current) {
      xtermRef.current.dispose();
      xtermRef.current = null;
    }

    setStatus('connecting');
    setErrorMessage(null);

    const term = new XTerm({
      cursorBlink: true,
      cursorStyle: 'block',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 13,
      lineHeight: 1.2,
      theme: {
        background: terminalBackground(),
        foreground: '#e6edf3',
        cursor: '#58a6ff',
        selectionBackground: 'rgba(56, 139, 253, 0.4)',
        black: '#484f58',
        red: '#ff7b72',
        green: '#3fb950',
        yellow: '#d29922',
        blue: '#58a6ff',
        magenta: '#bc8cff',
        cyan: '#39c5cf',
        white: '#b1bac4',
        brightBlack: '#6e7681',
        brightRed: '#ffa198',
        brightGreen: '#56d364',
        brightYellow: '#e3b341',
        brightBlue: '#79c0ff',
        brightMagenta: '#d2a8ff',
        brightCyan: '#56d4dd',
        brightWhite: '#f0f6fc',
      },
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(containerRef.current);
    fitAddon.fit();

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    const cols = term.cols || 80;
    const rows = term.rows || 24;
    const wsUrl = serverId ? api.getServerTerminalWebSocketURL(serverId, cols, rows) : api.getTerminalWebSocketURL(environmentId ?? '', cols, rows);

    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('connected');
      term.focus();
      term.write('\r\n\x1b[32mConnected to ' + environmentName + ' web terminal\x1b[0m\r\n\r\n');
    };

    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        term.write(event.data);
      } else if (event.data instanceof ArrayBuffer) {
        term.write(new Uint8Array(event.data));
      }
    };

    ws.onerror = () => {
      setStatus('error');
      setErrorMessage('WebSocket connection error');
      term.write('\r\n\x1b[31mConnection error.\x1b[0m\r\n');
    };

    ws.onclose = (event) => {
      setStatus('disconnected');
      if (event.code !== 1000) {
        term.write('\r\n\x1b[33mConnection closed (code ' + event.code + ').\x1b[0m\r\n');
      } else {
        term.write('\r\n\x1b[33mTerminal session ended.\x1b[0m\r\n');
      }
    };

    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(data);
      }
    });

    term.onResize(({ cols, rows }) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'resize', cols, rows }));
      }
    });
  }, [environmentId, serverId, environmentName]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  const clearTerminal = useCallback(() => {
    xtermRef.current?.clear();
    xtermRef.current?.focus();
  }, []);

  const handleResize = useCallback(() => {
    try {
      fitAddonRef.current?.fit();
    } catch {
      // Container may be hidden or resizing
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    const timer = setTimeout(handleResize, 100);
    return () => clearTimeout(timer);
  }, [isFullscreen, handleResize]);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (xtermRef.current) {
        xtermRef.current.dispose();
      }
    };
  }, []);

  const isOpen = status === 'connected' || status === 'connecting';

  const runShortcut = (command: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(command + '\r');
    xtermRef.current?.focus();
  };

  return (
    <div className={`ssh-terminal${isFullscreen ? ' ssh-terminal--fullscreen' : ''}`} data-testid="ssh-terminal">
      <div className="ssh-terminal__bar">
        <div className="ssh-terminal__title">
          <span>SSH terminal ({environmentName})</span>
          <StatusBadge kind="connection" value={status} />
          {errorMessage ? <span className="ssh-terminal__error">{errorMessage}</span> : null}
        </div>
        <div className="ssh-actions">
          {isOpen ? (
            <button type="button" className="button button--secondary button--small" onClick={disconnect}>
              Disconnect
            </button>
          ) : (
            <button type="button" className="button button--small" onClick={connect}>
              Connect
            </button>
          )}
          <button type="button" className="button button--secondary button--small" onClick={clearTerminal}>
            Clear
          </button>
          <button
            type="button"
            className="button button--secondary button--small"
            aria-pressed={isFullscreen}
            onClick={() => setIsFullscreen(!isFullscreen)}
          >
            {isFullscreen ? 'Exit full screen' : 'Full screen'}
          </button>
        </div>
      </div>

      {shortcuts && shortcuts.length > 0 ? (
        <div className="ssh-terminal__shortcuts" role="group" aria-label="Go to">
          <span className="muted" aria-hidden="true">Go to</span>
          {shortcuts.map((shortcut) => (
            <button
              key={shortcut.key}
              type="button"
              className="button button--ghost button--small"
              disabled={status !== 'connected'}
              title={status === 'connected' ? shortcut.command : 'Connect first'}
              onClick={() => runShortcut(shortcut.command)}
            >
              {shortcut.label}
            </button>
          ))}
        </div>
      ) : null}

      <div ref={containerRef} className="ssh-terminal__screen" />
    </div>
  );
}
